/**
 * Agent utility functions for resolving agent IDs across the application
 */

interface UserInfo {
  agent?: number;
  [key: string]: unknown;
}

interface LeadsPlusAgent {
  agentId?: number | string;
  [key: string]: unknown;
}

/**
 * Determines which agent IDs to use for a contact form submission
 *
 * Precedence order (documented in docs/agent-display-strategy.md):
 * 1. agentIds prop (if has values) - from CMS or listing data
 * 2. User's Associated Agent (if shouldAllowLeadsPlusAgent=true and userInfo.agent exists)
 * 3. Leads Plus Agent (if shouldAllowLeadsPlusAgent=true and leadsPlusAgent.agentId exists)
 * 4. Empty array (fallback)
 *
 * @param agentIds - Agent IDs from props (CMS config, listing agents, etc.)
 * @param shouldAllowLeadsPlusAgent - Whether to allow Leads Plus/User Agent fallback
 * @param userInfo - Redux user info object
 * @param leadsPlusAgent - Leads Plus agent from session storage/query param
 * @returns Array of agent IDs to submit with form
 */
export function getAgentIds(
  agentIds: number[],
  shouldAllowLeadsPlusAgent: boolean,
  userInfo: UserInfo | null,
  leadsPlusAgent: LeadsPlusAgent | null
): number[] {
  // Priority 1: Use agentIds prop if provided
  if (agentIds?.length > 0) {
    return Array.from(new Set(agentIds));
  }

  // Priority 2 & 3: Check Leads Plus feature flag
  if (shouldAllowLeadsPlusAgent) {
    // Priority 2: User's Associated Agent (permanent)
    if (userInfo?.agent) {
      return [userInfo.agent];
    }

    // Priority 3: Leads Plus Agent (temporary from query param)
    if (leadsPlusAgent?.agentId) {
      const agentId = Number(leadsPlusAgent.agentId);
      // Filter out invalid agent IDs (NaN)
      if (!isNaN(agentId)) {
        return [agentId];
      }
    }
  }

  // Priority 4: No agents available
  return [];
}
